import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper
} from '@mui/material';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';

const PasswordResetFlow = ({ onBackToSignIn, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await resetPassword({ username: email });
      console.log('✅ Password reset request successful');
      setCurrentStep('code');
    } catch (err) {
      console.error('❌ Password reset request failed:', err);
      setError(err.message || 'Failed to send reset code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    
    
    // Validate required fields
    if (!newPassword || newPassword.trim() === '') {
      setError('New password is required.');
      return;
    }
    
    if (!confirmPassword || confirmPassword.trim() === '') {
      setError('Please confirm your new password.');
      return;
    }
    
    if (!code || code.trim() === '') {
      setError('Verification code is required.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }
    
    // Validate password strength
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }
    
    try {
      await confirmResetPassword({ 
        username: email, 
        confirmationCode: code, 
        newPassword: newPassword 
      });
      console.log('✅ Password reset successful');
      setSuccess(true);
      setCurrentStep('success');
    } catch (err) {
      console.error('❌ Password reset confirmation failed:', err);
      setError(err.message || 'Failed to reset password. Please check your code and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToSignIn = () => {
    if (onBackToSignIn) {
      onBackToSignIn();
    }
  };

  const handleSuccess = () => {
    if (onSuccess) {
      onSuccess();
    } else {
      handleBackToSignIn();
    }
  };

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto', p: 3 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        {currentStep === 'email' && (
          <Box component="form" onSubmit={handleEmailSubmit}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
              Reset Your Password
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
              Enter your email address and we'll send you a verification code to reset your password.
            </Typography>
            
            <TextField
              fullWidth
              type="email"
              label="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              sx={{ mb: 3 }}
              disabled={loading}
            />
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || !email}
              sx={{ mb: 2 }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Sending Reset Code...
                </>
              ) : (
                'Send Reset Code'
              )}
            </Button>
            
            <Button
              fullWidth
              variant="text"
              onClick={handleBackToSignIn}
              disabled={loading}
            >
              ← Back to Sign In
            </Button>
          </Box>
        )}
        
        {currentStep === 'code' && (
          <Box component="form" onSubmit={handleCodeSubmit}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
              Enter Verification Code
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
              Enter the 6-digit code sent to your email and create a new password.
            </Typography>
            
            <TextField
              fullWidth
              type="text"
              label="Verification Code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="Enter 6-digit code"
              inputProps={{ maxLength: 6, pattern: '[0-9]*' }}
              required
              sx={{ mb: 3 }}
              disabled={loading}
            />
            
            <TextField
              fullWidth
              type="password"
              label="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              required
              sx={{ mb: 2 }}
              disabled={loading}
            />
            
            <TextField
              fullWidth
              type="password"
              label="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              sx={{ mb: 3 }}
              disabled={loading}
            />
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Security Notice:</strong> For your protection, password reset codes expire after 15 minutes.
              </Typography>
            </Alert>
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || !code || !newPassword || !confirmPassword}
              sx={{ mb: 2 }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Resetting Password...
                </>
              ) : (
                'Reset Password'
              )}
            </Button>
            
            <Button
              fullWidth
              variant="text"
              onClick={() => setCurrentStep('email')}
              disabled={loading}
            >
              ← Back to Email
            </Button>
          </Box>
        )}
        
        {currentStep === 'success' && (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'success.main' }}>
              Password Reset Successful!
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
              Your password has been reset successfully. You can now sign in with your new password.
            </Typography>
            
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Success:</strong> Your password has been updated in our secure system.
              </Typography>
            </Alert>
            
            <Button
              fullWidth
              variant="contained"
              onClick={handleSuccess}
            >
              Back to Sign In
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default PasswordResetFlow; 